import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  DocumentTextIcon,
  PencilIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { fetchMeetingMinutesById } from "../../../api/meetingMinutes";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import {
  MeetingMinutesShell,
  mmCardClass,
  mmPrimaryButtonClass,
  mmSecondaryButtonClass,
} from "./MeetingMinutesShell";
import { meetingTypeLabel } from "./MeetingMinutesTypePills";

export const MeetingMinutesView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [meetingMinutes, setMeetingMinutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchMeetingMinutesById(id);
        setMeetingMinutes(data);
      } catch (err) {
        setError(true);
        toast.error(t("meeting_minutes.load_error"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, t]);

  if (loading) return <ResourceLoading />;
  if (error || !meetingMinutes) return <ResourceError />;

  const meetings = meetingMinutes.meetings || [];

  return (
    <MeetingMinutesShell
      eyebrow={<><DocumentTextIcon className="h-4 w-4" />{t("meeting_minutes")}</>}
      title={meetingMinutes.title}
      subtitle={meetingMinutes.description}
      backTo="/dashboard/meeting-minutes"
      backLabel={t("Back")}
      actions={
        <>
          <Link to={`/dashboard/meeting-minutes/${id}/edit`} className={mmSecondaryButtonClass()}>
            <PencilIcon className="h-4 w-4" />
            {t("meeting_minutes.edit")}
          </Link>
          <button
            type="button"
            onClick={() => navigate(`/dashboard/meeting-minutes/${id}/meetings/new`)}
            className={mmPrimaryButtonClass()}
          >
            <PlusIcon className="h-4 w-4" />
            {t("meeting_minutes.add_meeting")}
          </button>
        </>
      }
    >
      <div className={`${mmCardClass()} mb-6 px-6 py-5`}>
        <div className="grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
          <div>
            <span className="font-semibold text-slate-900">{t("meeting_minutes.meeting_type")}: </span>
            {meetingTypeLabel(t, meetingMinutes.meeting_type)}
          </div>
          {meetingMinutes.project ? (
            <div>
              <span className="font-semibold text-slate-900">{t("meeting_minutes.project")}: </span>
              <Link to={`/dashboard/projects/${meetingMinutes.project.id}`} className="text-[#1463C7] hover:underline">
                {meetingMinutes.project.displayTitle || meetingMinutes.project.name}
              </Link>
            </div>
          ) : null}
          {meetingMinutes.employee ? (
            <div>
              <span className="font-semibold text-slate-900">{t("meeting_minutes.employee")}: </span>
              {meetingMinutes.employee.displayTitle}
            </div>
          ) : null}
        </div>
      </div>

      <div className={mmCardClass()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
          <h2 className="text-lg font-semibold text-slate-950">{t("meeting_minutes.meetings")}</h2>
        </div>
        {meetings.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="mb-4 text-sm text-slate-500">{t("meeting_minutes.no_meetings")}</p>
            <button
              type="button"
              onClick={() => navigate(`/dashboard/meeting-minutes/${id}/meetings/new`)}
              className={mmPrimaryButtonClass()}
            >
              <PlusIcon className="h-4 w-4" />
              {t("meeting_minutes.add_first_meeting")}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {meetings.map((meeting) => (
              <Link
                key={meeting.id}
                to={`/dashboard/meeting-minutes/${id}/meetings/${meeting.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-slate-50"
              >
                <div>
                  <h3 className="text-sm font-semibold text-slate-950">
                    {t("meeting_minutes.meeting_number")} {meeting.meeting_number}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {meeting.meeting_date}
                    {meeting.groups?.length
                      ? ` · ${meeting.groups.length} ${t("meeting_minutes.points")}`
                      : ""}
                    {meeting.last_sent_at ? ` · ${t("documentationSend.sent_short")}` : ""}
                  </p>
                </div>
                {meeting.is_finished ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                    {t("meeting_minutes.meeting.finished")}
                  </span>
                ) : (
                  <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                    {t("meeting_minutes.status.draft")}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </MeetingMinutesShell>
  );
};
